import React, {useCallback, useEffect, useState} from 'react';
import {QRCodeSVG} from 'qrcode.react';
import createAxiosInstance from '../support/AxiosSupport';
import {formatDateTime, parseApiDate} from '../utils/dateTime';
import Spinner from './Spinner';
import {SendIcon, RefreshIcon, TrashIcon} from './icons';

/**
 * Раздел «Привязка Telegram» в настройках профиля (CTHS-52, Epic CTHS-3).
 *
 * Изолирован в отдельный компонент ради тестируемости state-машины:
 * `loading → not-linked / pending / linked / error`.
 *
 * Работает поверх backend REST API (CTHS-51), контракт:
 *   GET    /telegram/status  → 200 { linked, username, linkedAt }
 *   POST   /telegram/link    → 200 { code, deepLink, qrPayload, expiresAt }
 *   DELETE /telegram/link    → 204
 *
 * `deepLink` и `qrPayload` приходят с backend — URL бота не хардкодится (C-4.5).
 */

interface TelegramStatusDto {
    linked: boolean;
    username: string | null;
    linkedAt: string | null;
}

interface TelegramLinkDto {
    code: string;
    deepLink: string;
    qrPayload: string;
    expiresAt: string | null;
}

type Phase = 'loading' | 'not-linked' | 'pending' | 'linked' | 'error';

const computeSecondsLeft = (expiresAt: string | null): number | null => {
    const d = parseApiDate(expiresAt);
    if (!d) {
        return null;
    }
    return Math.max(0, Math.round((d.getTime() - Date.now()) / 1000));
};

const formatCountdown = (seconds: number): string => {
    const mm = Math.floor(seconds / 60).toString().padStart(2, '0');
    const ss = (seconds % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
};

const formatLinkedAt = (iso: string | null): string => formatDateTime(iso, undefined, '');

const TelegramLinkSection: React.FC = () => {
    const [phase, setPhase] = useState<Phase>('loading');
    const [status, setStatus] = useState<TelegramStatusDto | null>(null);
    const [linkData, setLinkData] = useState<TelegramLinkDto | null>(null);
    const [countdown, setCountdown] = useState<number | null>(null);
    const [linking, setLinking] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const [unlinkConfirm, setUnlinkConfirm] = useState(false);

    // Однократная проверка статуса: используется и поллингом, и кнопкой «Проверить».
    const checkStatusOnce = useCallback(async () => {
        const api = await createAxiosInstance();
        const res = await api.get<TelegramStatusDto>('/telegram/status');
        if (res.data.linked) {
            setStatus(res.data);
            setPhase('linked');
        }
    }, []);

    const loadStatus = useCallback(async () => {
        setPhase('loading');
        try {
            const api = await createAxiosInstance();
            const res = await api.get<TelegramStatusDto>('/telegram/status');
            if (res.data.linked) {
                setStatus(res.data);
                setPhase('linked');
            } else {
                setPhase('not-linked');
            }
        } catch {
            setPhase('error');
        }
    }, []);

    const requestCode = useCallback(async () => {
        setLinking(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<TelegramLinkDto>('/telegram/link');
            setLinkData(res.data);
            setCountdown(computeSecondsLeft(res.data.expiresAt));
            setPhase('pending');
        } catch {
            setPhase('error');
        } finally {
            setLinking(false);
        }
    }, []);

    const unlink = useCallback(async () => {
        setUnlinking(true);
        try {
            const api = await createAxiosInstance();
            await api.delete('/telegram/link');
            setStatus(null);
            setLinkData(null);
            setUnlinkConfirm(false);
            setPhase('not-linked');
        } catch {
            setPhase('error');
        } finally {
            setUnlinking(false);
        }
    }, []);

    // Начальная загрузка статуса (AC1).
    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // TTL-обратный отсчёт с cleanup (AC2/AC3). Если expiresAt отсутствует — таймер не стартует.
    useEffect(() => {
        if (phase !== 'pending' || !linkData?.expiresAt) {
            return;
        }
        const expiresAt = linkData.expiresAt;
        const id = setInterval(() => {
            const left = computeSecondsLeft(expiresAt) ?? 0;
            setCountdown(left);
            if (left === 0) {
                clearInterval(id);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [phase, linkData?.expiresAt]);

    // Автополлинг статуса в состоянии pending раз в 3 сек с cleanup при unmount (AC4).
    useEffect(() => {
        if (phase !== 'pending') {
            return;
        }
        const id = setInterval(() => {
            checkStatusOnce().catch(() => {
                /* транзиентная ошибка — повторим на следующем тике */
            });
        }, 3000);
        return () => clearInterval(id);
    }, [phase, checkStatusOnce]);

    const expired = phase === 'pending' && countdown === 0;

    const renderBody = (): React.ReactNode => {
        if (phase === 'loading') {
            return (
                <div data-testid="telegram-loading">
                    <Spinner/>
                </div>
            );
        }

        if (phase === 'error') {
            return (
                <div data-testid="telegram-error" className="space-y-3">
                    <p className="text-sm text-red-600 dark:text-red-400">
                        Не удалось загрузить состояние привязки Telegram.
                    </p>
                    <button onClick={loadStatus} className="btn-primary min-h-[44px]">
                        <RefreshIcon className="inline-block mr-2" size={18}/>
                        Повторить
                    </button>
                </div>
            );
        }

        if (phase === 'linked') {
            return (
                <div data-testid="telegram-linked" className="space-y-3">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        {status?.username
                            ? <>Аккаунт привязан: <span className="font-mono font-semibold">{status.username}</span></>
                            : 'Аккаунт привязан'}
                    </p>
                    {status?.linkedAt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Дата привязки: {formatLinkedAt(status.linkedAt)}
                        </p>
                    )}
                    {!unlinkConfirm ? (
                        <button
                            onClick={() => setUnlinkConfirm(true)}
                            className="btn-danger min-h-[44px]"
                            data-testid="telegram-unlink"
                        >
                            <TrashIcon className="inline-block mr-2" size={18}/>
                            Отвязать
                        </button>
                    ) : (
                        <div className="space-y-2" data-testid="telegram-unlink-confirm">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Отвязать Telegram-аккаунт? Уведомления перестанут приходить в Telegram.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={unlink}
                                    disabled={unlinking}
                                    className="btn-danger min-h-[44px]"
                                    data-testid="telegram-unlink-confirm-yes"
                                >
                                    {unlinking ? 'Отвязываем...' : 'Да, отвязать'}
                                </button>
                                <button
                                    onClick={() => setUnlinkConfirm(false)}
                                    disabled={unlinking}
                                    className="btn-secondary min-h-[44px]"
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (phase === 'pending' && linkData) {
            if (expired) {
                return (
                    <div data-testid="telegram-expired" className="space-y-3">
                        <p className="text-sm text-gray-700 dark:text-gray-300">Код истёк</p>
                        <button
                            onClick={requestCode}
                            disabled={linking}
                            className="btn-primary min-h-[44px]"
                            data-testid="telegram-refresh-code"
                        >
                            <RefreshIcon className="inline-block mr-2" size={18}/>
                            {linking ? 'Обновляем...' : 'Обновить код'}
                        </button>
                    </div>
                );
            }
            return (
                <div data-testid="telegram-pending" className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Откройте Telegram и отправьте боту код, либо отсканируйте QR-код.
                    </p>
                    <div className="flex flex-col items-start gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Одноразовый код</span>
                        <span
                            className="font-mono text-2xl tracking-widest text-gray-900 dark:text-gray-100"
                            data-testid="telegram-code"
                        >
                            {linkData.code}
                        </span>
                    </div>
                    <div
                        className="inline-flex bg-white dark:bg-gray-100 p-3 rounded-md"
                        data-testid="telegram-qr"
                    >
                        <QRCodeSVG value={linkData.qrPayload} size={160}/>
                    </div>
                    {countdown !== null && (
                        <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="telegram-countdown">
                            Код действует ещё {formatCountdown(countdown)}
                        </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                        <a
                            href={linkData.deepLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary min-h-[44px] inline-flex items-center"
                            data-testid="telegram-deeplink"
                        >
                            <SendIcon className="inline-block mr-2" size={18}/>
                            Открыть в Telegram
                        </a>
                        <button
                            onClick={() => checkStatusOnce().catch(() => undefined)}
                            className="btn-secondary min-h-[44px]"
                            data-testid="telegram-check"
                        >
                            Проверить
                        </button>
                    </div>
                </div>
            );
        }

        // phase === 'not-linked'
        return (
            <div data-testid="telegram-not-linked" className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Привяжите Telegram-аккаунт, чтобы получать уведомления гардероба в мессенджере.
                </p>
                <button
                    onClick={requestCode}
                    disabled={linking}
                    className="btn-primary min-h-[44px]"
                    data-testid="telegram-link-start"
                >
                    <SendIcon className="inline-block mr-2" size={18}/>
                    {linking ? 'Готовим код...' : 'Привязать Telegram'}
                </button>
            </div>
        );
    };

    return (
        <div className="card p-6 mt-6" data-testid="telegram-section">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Telegram
            </h3>
            {renderBody()}
        </div>
    );
};

export default TelegramLinkSection;
